import { TableCell, tableCellClasses, TableRow, styled } from '@mui/material';

import React from 'react';

import styles from './CommonTable.module.scss';

const { textColor, tableOddRowColor, tableHeadColumRowColor } = styles;

export const StyledTableCell = styled(TableCell)(() => ({
	[`&.${tableCellClasses.head}`]: {
		backgroundColor: 'white',
		border: 'none',
		color: textColor,
		fontSize: 14,
		fontWeight: 400,
		lineHeight: '14px',
		textWrap: 'nowrap',
	},
	[`&.${tableCellClasses.body}`]: {
		fontSize: 14,
		border: 'none',
		color: textColor,
		lineHeight: '14px',
	},
}));

export const StyledTableRow = styled(TableRow)(({ theme }) => ({
	'&:nth-of-type(odd)': {
		backgroundColor: theme.palette.action.hover,
		borderRadius: '4px',
	},
	// hide last border
	'&:last-child td, &:last-child th': {
		border: 0,
	},
}));

export const tableThemes = [
	{ headColor: tableHeadColumRowColor, bodyColor: tableOddRowColor },
	{ headColor: tableOddRowColor, bodyColor: tableHeadColumRowColor },
];
type IEditableProps =
	| {
			renderEditCell: (value: any) => React.ReactElement | React.FC;
			isCellEditable: false;
	  }
	| {
			renderEditCell: (value: any) => React.ReactElement | React.FC;
			isCellEditable: true;
	  }
	| {
			renderEditCell?: never;
			isCellEditable?: false;
	  };

type IRowHoverEditableProps =
	| {
			renderHoverEditCell?: (value: any) => React.ReactElement | React.FC;
			isCellHoverEditable: false;
	  }
	| {
			renderHoverEditCell?: (value: any) => React.ReactElement | React.FC;
			isCellHoverEditable: true;
	  }
	| {
			renderHoverEditCell?: never;
			isCellHoverEditable?: false;
	  };

export type Column = {
	id: string;
	label: string;
	render?: (value: any) => React.ReactElement;
	disablePadding: boolean;
	isSortable?: boolean;
	cellClassName?: string;
	isHidden?: boolean;
	hideSentenceCase?: boolean;
	isAlignRight?: boolean;
} & IEditableProps &
	IRowHoverEditableProps;

export interface Pagination {
	pageNumber: number;
	pageSize: number;
	sortKey?: string;
	sortDirection?: string;
}
export interface ICommonTableProps {
	id?: string;
	rows: any;
	columns: any;
	defaultSortKey?: string; // default sort key
	tableStyles?: any;
	isCheckBoxEnabled?: boolean;
	onRowSelectable?: (data: any) => void;
	onRowChecked?: (data: any) => void;
	enableHeaderColor?: boolean;
	hidePagination?: boolean;
	enableInnerTableRow?: boolean;
	customStylesEnabled?: boolean;
}

export interface ICommonTableOneProps {
	rows: any;
	columns: any;
	isRenderPagination: boolean;
	defaultSortKey?: string; // default sort key
	defaultSortDirection?: Order;
	tableStyles?: any;
	isCheckBoxEnabled?: boolean;
	rowKey?: string;
	onRowSelectable?: (data: any) => void;
	onRowChecked?: (data: any) => void;
	clearSelection?: number;
	totalItems: number;
	searchKey?: string;
	sortKey?: string;
	sortDirection?: string;
	onPageHandler?: (data: any, clickFlag?: boolean) => void;
	isLoading?: boolean;
	totalPagesCount?: number;
	onSortHandler?: (data: any) => void;
	rowHoverEnabled?: boolean;
	customErrorMessage?: string;
	isError?: boolean;
	enableHeaderColor?: boolean;
	enableInnerTableRow?: boolean;
	enableInnerTableTemplate?: (
		value: any,
	) => React.ReactElement | React.JSX.Element;
	defaultQuery?: any;
	colSpan?: number;
	hideSelectPageOption?: boolean;
	isFlatStyle?: boolean;
}

export type Order = 'asc' | 'desc';

export const commonTableMockTestData: any[] = [
	{
		id: '1234',
		officeName: '12 CFR Part 1026',
		regulator: 'CFPB',
		modifiedDate: '04/19/23',
		risks: 21,
		processes: 609,
		controls: 609,
	},
	{
		id: '1235',
		officeName: '12 CFR Part 1027',
		regulator: 'CFPB',
		modifiedDate: '04/20/23',
		risks: 22,
		processes: 509,
		controls: 691,
	},
	{
		id: '1236',
		officeName: '12 CFR Part 1028',
		regulator: 'CFPB',
		modifiedDate: '04/21/23',
		risks: 23,
		processes: 409,
		controls: 692,
	},
	{
		id: '1237',
		officeName: '12 CFR Part 1029',
		regulator: 'CFPB',
		modifiedDate: '04/22/23',
		risks: 24,
		processes: 309,
		controls: 693,
	},
	{
		id: '1238',
		officeName: '12 CFR Part 1030',
		regulator: 'CFPB',
		modifiedDate: '04/23/23',
		risks: 25,
		processes: 209,
		controls: 694,
	},
	{
		id: '1239',
		officeName: '12 CFR Part 1031',
		regulator: 'CFPB',
		modifiedDate: '04/24/23',
		risks: 26,
		processes: 109,
		controls: 695,
	},
	{
		id: '1240',
		officeName: '12 CFR Part 1032',
		regulator: 'CFPB',
		modifiedDate: '04/25/23',
		risks: 27,
		processes: 99,
		controls: 696,
	},
	{
		id: '1241',
		officeName: '12 CFR Part 1033',
		regulator: 'CFPB',
		modifiedDate: '04/26/23',
		risks: 28,
		processes: 89,
		controls: 697,
	},
	{
		id: '1242',
		officeName: '12 CFR Part 1034',
		regulator: 'CFPB',
		modifiedDate: '04/27/23',
		risks: 29,
		processes: 79,
		controls: 698,
	},
	{
		id: '1243',
		officeName: '12 CFR Part 1035',
		regulator: 'CFPB',
		modifiedDate: '04/28/23',
		risks: 30,
		processes: 69,
		controls: 699,
	},
	{
		id: '1244',
		officeName: '12 CFR Part 1036',
		regulator: 'CFPB',
		modifiedDate: '04/29/23',
		risks: 31,
		processes: 59,
		controls: 269,
	},
];

export const mockColumns: readonly any[] = [
	{
		id: 'riskName',
		label: 'Risk Name',
		disablePadding: false,
		render: (params: any) => (
			<span>
				<a href="/">{params.riskName}</a>
			</span>
		),
	},
	{ id: 'description', label: 'Description', disablePadding: false },
	{ id: 'likelihood', label: 'Likelihood', disablePadding: false },
	{ id: 'impact', label: 'Impact', disablePadding: false },
	{ id: 'riskRating', label: 'Risk Rating', disablePadding: false },
];

export const mockServerColumns: readonly any[] = [
	{
		id: 'regOfficialId',
		label: 'Regulation',
		disablePadding: false,
		isCellEditable: true,
		renderEditCell: (params: any) => (
			<span>
				<a href="/">{params.regOfficialId}</a>
			</span>
		),
		render: (params: any) => (
			<span>
				<a href="/">{params.regOfficialId}</a>
			</span>
		),
	},
	{
		id: 'regulationTitle',
		label: 'Regulation Title',
		disablePadding: false,
		isCellHoverEditable: true,
		renderHoverEditCell: (params: any) => (
			<span>
				<a href="/">{params.regOfficialId}</a>
			</span>
		),
	},
	{ id: 'agency', label: 'Agency', disablePadding: false },
	{ id: 'citationTitle', label: 'CitationTitle', disablePadding: false },
	{ id: 'priority', label: 'Priority', disablePadding: false },
];
export const commonTableServerTestData: any[] = [
	{
		regulationId: '9b783064-953f-410c-bbf4-ce1f7996b909',
		regOfficialId: '12 CFR Part 1026',
		agency: 'CFPB',
		regulationTitle: ' Part 1026 Truth in Lending (Regulation Z)',
		citationId: '3243009f-f802-4d01-bc77-5f65b833ad6d',
		citationTitle: '12 CFR 1026.17.i',
		assignedUser: 'Matt ElSayed',
		priority: 'Low',
		taskTotalCount: 8,
		taskOpenCount: 8,
		status: null,
		reviewDeadLine: null,
	},
	{
		regulationId: '9b783064-953f-410c-bbf4-ce1f7996b909',
		regOfficialId: '12 CFR Part 1026',
		agency: 'CFPB',
		regulationTitle: ' Part 1026 Truth in Lending (Regulation Z)',
		citationId: '28386589-fdfc-4d5a-8eb3-e4aa7860a91b',
		citationTitle: '12 CFR 1026.17.a',
		assignedUser: 'Sean Smit',
		priority: 'Low',
		taskTotalCount: 0,
		taskOpenCount: 0,
		status: null,
		reviewDeadLine: null,
	},
	{
		regulationId: '9b783064-953f-410c-bbf4-ce1f7996b909',
		regOfficialId: '12 CFR Part 1026',
		agency: 'CFPB',
		regulationTitle: ' Part 1026 Truth in Lending (Regulation Z)',
		citationId: 'a8c3afb9-9df1-4fdb-b53b-c85951b60a8b',
		citationTitle: '12 CFR 1026.17.c.3.ii',
		assignedUser: 'Matt ElSayed',
		priority: 'Low',
		taskTotalCount: 12,
		taskOpenCount: 12,
		status: null,
		reviewDeadLine: null,
	},
	{
		regulationId: '9b783064-953f-410c-bbf4-ce1f7996b909',
		regOfficialId: '12 CFR Part 1026',
		agency: 'CFPB',
		regulationTitle: ' Part 1026 Truth in Lending (Regulation Z)',
		citationId: '0b245d25-b139-48a9-9691-97a81967055c',
		citationTitle: '12 CFR 1026.17.c.6.i',
		assignedUser: 'Matt ElSayed',
		priority: 'Medium',
		taskTotalCount: 5,
		taskOpenCount: 5,
		status: null,
		reviewDeadLine: null,
	},
	{
		regulationId: '9b783064-953f-410c-bbf4-ce1f7996bd909',
		regOfficialId: '12 CFR Part 1026',
		agency: 'CFPB',
		regulationTitle: ' Part 1026 Truth in Lending (Regulation Z)',
		citationId: 'ff357b1d-907d-4b13-82d2-6e98f3432c6f',
		citationTitle: '12 CFR 1026.17.c',
		assignedUser: null,
		priority: 'Low',
		taskTotalCount: 0,
		taskOpenCount: 0,
		status: null,
		reviewDeadLine: null,
	},
	{
		regulationId: '9b783064-953f-410c-bbf4-ce1fd7996b909',
		regOfficialId: '12 CFR Part 1026',
		agency: 'CFPB',
		regulationTitle: ' Part 1026 Truth in Lending (Regulation Z)',
		citationId: '30b47c85-6d30-4973-8f28-bd3ce20f928e',
		citationTitle: '12 CFR 1026.17.c.3.i',
		assignedUser: 'Matt ElSayed',
		priority: 'High',
		taskTotalCount: 72,
		taskOpenCount: 64,
		status: null,
		reviewDeadLine: null,
	},
	{
		regulationId: '9b783064-953f-410c-bbf4-ce1f7996b909d',
		regOfficialId: '12 CFR Part 1026',
		agency: 'CFPB',
		regulationTitle: ' Part 1026 Truth in Lending (Regulation Z)',
		citationId: '8e39061b-95c7-4515-884f-77d2e3b3d8ff',
		citationTitle: '12 CFR 1026.17.c.3.iv',
		assignedUser: 'Matt ElSayed',
		priority: 'Low',
		taskTotalCount: 0,
		taskOpenCount: 0,
		status: null,
		reviewDeadLine: null,
	},
	{
		regulationId: '9b783064-953f-410c-bbf4-ce1f7996b909v',
		regOfficialId: '12 CFR Part 1026',
		agency: 'CFPB',
		regulationTitle: ' Part 1026 Truth in Lending (Regulation Z)',
		citationId: '17cf92d5-06b9-40b0-be86-e36dd294a711',
		citationTitle: '12 CFR 1026.17.b',
		assignedUser: 'Matt ElSayed',
		priority: 'Low',
		taskTotalCount: 0,
		taskOpenCount: 0,
		status: null,
		reviewDeadLine: null,
	},
	{
		regulationId: '9b783064-953f-410c-bbf4-ce1f7996b909vb',
		regOfficialId: '12 CFR Part 1026',
		agency: 'CFPB',
		regulationTitle: ' Part 1026 Truth in Lending (Regulation Z)',
		citationId: '37afa109-1fc5-4896-99fb-4365c36a7f3e',
		citationTitle: '12 CFR 1026.17',
		assignedUser: null,
		priority: 'Low',
		taskTotalCount: 0,
		taskOpenCount: 0,
		status: null,
		reviewDeadLine: null,
	},
	{
		regulationId: '9b783064-953f-410c-bbf4-ce1f7996b90ds9',
		regOfficialId: '12 CFR Part 1026',
		agency: 'CFPB',
		regulationTitle: ' Part 1026 Truth in Lending (Regulation Z)',
		citationId: '67ed66fc-3ff2-4b44-b082-769a6523ceb4',
		citationTitle: '12 CFR 1026.17.a.2',
		assignedUser: 'Matt ElSayed',
		priority: 'Low',
		taskTotalCount: 1,
		taskOpenCount: 1,
		status: null,
		reviewDeadLine: null,
	},
];

export const onPageTextChanged = (
	value: number,
	rowsPerPage?: number,
	totalItems?: number,
	totalPagesCount?: number,
) => {
	let result = value;
	let pagesCount = 0;
	if (totalPagesCount) {
		pagesCount = totalPagesCount;
	} else {
		pagesCount = totalItems && rowsPerPage ? totalItems / rowsPerPage : 0;
	}
	if (Number.isNaN(value) || value < 1) {
		result = 1;
	} else if (value > pagesCount) {
		result = Math.ceil(pagesCount);
	}
	return result;
};
